import React, { useEffect, useRef, useState } from 'react';
import Button from './Button';
import './AlertCard.css';
import { StatusPill, StatusStripe } from './Status';

// Snooze duration menu choices. Numeric value = days. Kept narrow on
// purpose — operators don't need a calendar picker for an inline
// suppression action.
const SNOOZE_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' }
];

function SnoozeMenu({ disabled, onChoose }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click / Escape so the menu can't get stuck.
  useEffect(() => {
    if (!open) return undefined;
    const handleDoc = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleDoc);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDoc);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="alert-card-snooze-wrap" ref={wrapperRef}>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className="alert-card-snooze"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Snooze alert"
        data-testid="alert-card-snooze"
      >
        Snooze ▾
      </Button>
      {open ? (
        <div role="menu" className="alert-card-snooze-menu" data-testid="alert-card-snooze-menu">
          {SNOOZE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              role="menuitem"
              className="alert-card-snooze-option"
              data-testid={`alert-card-snooze-${opt.value}`}
              onClick={() => {
                setOpen(false);
                onChoose(opt.value);
              }}
              disabled={disabled}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Compact alert row used by Alerts Center and (eventually) the
 * Executive Snapshot "Needs attention" section.
 *
 * Input is a serialized Alert from /api/alerts:
 *   { id, category, severity, title, subtitle, value, cta }
 *
 * Pure presentational — no data fetching, no local state. Click handlers
 * for the CTA are injected by the parent so the component stays reusable
 * across routing styles (path-based today, any future router tomorrow).
 */
function AlertCard({ alert, onCtaClick, onDismiss, onSnooze, busy = false }) {
  if (!alert) return null;
  const severity = alert.severity || 'info';
  const icon = severityIcon(severity);
  const handleCta = (event) => {
    if (!alert.cta || typeof onCtaClick !== 'function') return;
    event.preventDefault();
    onCtaClick(alert.cta, alert);
  };
  const handleDismiss = (event) => {
    if (typeof onDismiss !== 'function') return;
    event.preventDefault();
    onDismiss(alert);
  };
  const handleSnoozeChoice = (days) => {
    if (typeof onSnooze !== 'function') return;
    onSnooze(alert, { days });
  };

  return (
    <div className={`alert-card alert-card--${severity}`} data-testid="alert-card">
      <StatusStripe tone={severity} className="alert-card-rail" aria-hidden />
      <div className="alert-card-body">
        <div className="alert-card-head">
          <span className={`alert-card-icon alert-card-icon--${severity}`} aria-hidden>
            {icon}
          </span>
          <StatusPill tone={severity} className="alert-card-severity">
            {severityLabel(severity)}
          </StatusPill>
          <span className="alert-card-category">{categoryLabel(alert.category)}</span>
        </div>
        <div className="alert-card-title">{alert.title}</div>
        {alert.subtitle ? <div className="alert-card-sub">{alert.subtitle}</div> : null}
      </div>
      <div className="alert-card-side">
        {alert.value ? <div className="alert-card-value">{alert.value}</div> : null}
        <div className="alert-card-actions">
          {alert.cta ? (
            <Button size="sm" variant="secondary" onClick={handleCta} className="alert-card-cta" disabled={busy}>
              {primaryCtaLabel(alert)}
            </Button>
          ) : null}
          {typeof onSnooze === 'function' ? (
            <SnoozeMenu disabled={busy} onChoose={handleSnoozeChoice} />
          ) : null}
          {typeof onDismiss === 'function' ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDismiss}
              disabled={busy}
              className="alert-card-dismiss"
              aria-label="Dismiss alert"
              data-testid="alert-card-dismiss"
            >
              Dismiss
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function severityIcon(severity) {
  if (severity === 'critical') return '!';
  if (severity === 'warn') return '!';
  if (severity === 'good') return '✓';
  return 'i';
}

function primaryCtaLabel(alert) {
  const category = String(alert?.category || '');
  if (category === 'approval-aging') {
    return 'Approve';
  }
  if (category === 'kpi-behind' || category === 'kpi-missing' || category === 'kpi-streak') {
    return 'View KPI';
  }
  return 'Review';
}

function severityLabel(severity) {
  if (severity === 'critical') return 'Action needed';
  if (severity === 'warn') return 'Watch this';
  if (severity === 'info') return 'Heads up';
  if (severity === 'good') return 'On a streak';
  return 'Alert';
}

function categoryLabel(category) {
  const map = {
    'kpi-behind': 'KPI behind',
    'kpi-missing': 'Missing actuals',
    'approval-aging': 'Approvals',
    'attendance-risk': 'Attendance',
    'kpi-streak': 'Top performer',
    'cert-expiration': 'Certification'
  };
  return map[category] || 'Alert';
}

export default AlertCard;
